/**
 * Document type keys accepted by the unified document pipeline
 * (WebAPI DocumentPipelineController / DocumentTypeRegistry).
 *
 * Values are the API's shared DocumentConstants — the same strings as the
 * Document table's Description column and the tms-web ReportNames enum, so
 * there is one naming scheme across the stack. A document type must also be
 * enabled in the API's DocumentPipelineSettings.EnabledDocumentTypes before
 * it can be used.
 *
 * HOW THIS DIFFERS FROM ReportNames (tms-web shared/constants/report-names.ts):
 * ReportNames addresses Document TABLE rows — getDocument() email messages and
 * getUserPrinterForDocument() printer config — and predates the pipeline.
 * PipelineDocumentTypes addresses the RENDER registry: which server-side
 * document to produce. Most values coincide (one naming scheme), but each list
 * has entries the other lacks (e.g. pipeline-only "Sales Picking List",
 * "Picked Customer Stock Labels"; ReportNames-only rows that only carry email
 * text). They can merge once the legacy client-side report paths are deleted.
 */
export const PipelineDocumentTypes = {
  salesInvoice: 'Sales Invoice',
  creditNote: 'Credit Note',
  salesOrderConfirmation: 'Sales Order Confirmation',
  salesDespatchNote: 'Sales Despatch Note',
  proformaInvoice: 'Proforma Invoice',
  purchaseOrder: 'Purchase Order',
  pickingList: 'Picking List',
  purchaseDespatch: 'Purchase Despatch',
  goodsDeliveryNote: 'Goods Delivery Note',
  salesManifest: 'Sales Manifest',
  commercialInvoice: 'Commercial Invoice',
  archiveInvoice: 'Archive Invoice',
  supplementaryInvoice: 'Supplementary Invoice',
  availableStockPickingList: 'Available Stock Picking List',
  salesOrderBalances: 'Sales Order Balances',
  salesBackOrder: 'Sales Back Order',
  paymentInvoice: 'Payment Invoice',
  // The sales-order picking list — a different document from pickingList
  // (the purchase-order one), despite the similar name.
  salesPickingList: 'Sales Picking List',
  // The by-PICK-LIST picking documents (standalone picking system): the
  // despatch style renders the same order sections as salesPickingList with
  // the pick list number as barcode; the gather style is the flat QR-coded
  // gather sheet (Transfer / SourcePick / ManufacturingMaterials lists).
  despatchPickList: 'Despatch Picking List',
  gatherPickList: 'Gather Picking List',
  // The two halves of the transfer paperwork (keyed by pick list id): the
  // packing list the receiving warehouse checks the pallet in against, and the
  // customs invoice that crosses the border with it. Distinct from
  // goodsDeliveryNote / commercialInvoice, the stock-delivery pair.
  transferPackingList: 'Transfer Packing List',
  transferCommercialInvoice: 'Transfer Commercial Invoice',
  // The transfer REQUEST document (keyed by request header id): what the office asked to be
  // moved, with the rolls allocated to each line. A different document from the two above,
  // which describe the pallet that carries it.
  transferRequest: 'Transfer Request',
  releaseOrder: 'Release Order',
  salesOrderCollection: 'Sales Order Collection',
  stockLabel: 'Stock Label',
  // The Stock Label for pieces LOADED on a transfer (keyed by PickListLineStock id): the same
  // label, printing the quantity going on the transfer. Prints on the Stock Label printer.
  transferStockLabel: 'Transfer Stock Label',
  // The Stock Label for woven PIECES (keyed by piece id, Steve 2026-10-29): the piece number, the
  // finished length ordered and the Mill No. Its own Document row and printer.
  pieceLabel: 'Piece Label',
  salesContract: 'Sales Contract',
  salesPack: 'Sales Pack',
  purchaseInvoice: 'Purchase Invoice',
  purchaseContract: 'Purchase Contract',
  // The third-party despatch note on the purchase order Reports tab.
  despatchNote: 'Despatch Note',
  // The purchase-receipt goods received note; its reportId is the delivery
  // note NUMBER (a string), which the pipeline sends as reportKey.
  purchaseDeliveryNote: 'Purchase Delivery Note',
  releaseStockDocument: 'Release Stock Document',
  productSpecPrint: 'Product Spec Print',
  // The one LANDSCAPE document - its Document row carries 297 x 210 and the pipeline
  // renders it on its side (keyed by the weaving JOB id).
  weavingTicket: 'Weaving Ticket',
  // The weaving ticket for a DESIGN (keyed by the design id), landscape like the ticket,
  // with each colourway's warp and weft yarns on a second page.
  mastercard: 'Mastercard',
  // The allocated yarn to pull for a warping/weaving job pair (keyed by either job's id).
  yarnPickingList: 'Yarn Picking List',
  locationLabel: 'Location Label',
  // All PICKED customer stock labels for a sales order, one label per page.
  pickedCustomerStockLabels: 'Picked Customer Stock Labels',
  // Customer stock labels for a despatch note's items, one label per page.
  despatchCustomerStockLabels: 'Despatch Customer Stock Labels',
  // Delivery address label (with the Order No barcode) for a despatch note.
  addressLabel: 'Address Label',
  // Supplier stock labels for stock allocated to a purchase order's BOMs.
  supplierStockLabels: 'Supplier Stock Labels',
  // Every stock label for one receipt (keyed by GoodsReceivedNote id) — the
  // batch reprint of what the printers got at goods-in.
  receivedStockLabels: 'Received Stock Labels',
  // Labels for NAMED stock allocations — the picking and cutting screens print
  // for the allocation(s) in hand, not for a whole order or despatch note.
  allocatedCustomerStockLabels: 'Allocated Customer Stock Labels',
  allocatedSupplierStockLabels: 'Allocated Supplier Stock Labels',
  // The address label prints from several screens, each holding a different id;
  // addressLabel above stays the despatch-note flavour.
  salesOrderAddressLabel: 'Sales Order Address Label',
  purchaseOrderAddressLabel: 'Purchase Order Address Label',
  purchaseOrderDespatchAddressLabel: 'Purchase Order Despatch Address Label',
  customerAddressLabel: 'Customer Address Label',
  supplierAddressLabel: 'Supplier Address Label',
  deliveryAddressLabel: 'Delivery Address Label',
  // Sample labels, keyed by product id.
  sampleLabel: 'Sample Label',
  // The 50 x 40 mm label naming what a finished item is. Keyed by a PRODUCT id from the
  // Documents tab, or by received PIECE ids on receipt (one label per item the piece holds).
  productLabel: 'Product Label',
} as const;
